/** The format version this release writes. Older files are migrated forward on load. */
export const FORMAT_VERSION = 1;

/** Reserved `data` namespace for editor-only state (locked, hidden). The runtime ignores it. */
export const EDITOR_NAMESPACE = 'animo.editor';

export const CORE_TYPES = [
  'image', 'vector', 'sprite', 'text', 'bitmapText', 'nineSlice', 'zone', 'tileSprite', 'particles', 'light', 'video', 'container', 'tilemapLayer', 'prefab',
] as const;
export type CoreType = (typeof CORE_TYPES)[number];

export function isCoreType(type: string): type is CoreType {
  return (CORE_TYPES as readonly string[]).includes(type);
}

/** Plugin ids and plugin object types are dotted, such as `mygame.enemy`. */
export const PLUGIN_ID_PATTERN = /^[a-z0-9][a-z0-9_-]*(\.[A-Za-z0-9_-]+)+$/;

/** Separator in runtime id paths and override keys (`o9b2/p3`). */
export const ID_PATH_SEPARATOR = '/';

/** Fields the placement of a prefab sets on the prefab's root, replacing the root's own. */
export const PLACEMENT_TRANSFORM_FIELDS = ['x', 'y', 'scaleX', 'scaleY', 'angle', 'depth'] as const;

/** Fields every display object accepts, by type. Used for key order and unknown-field warnings. */
export const DISPLAY_FIELDS = [
  'x', 'y', 'scaleX', 'scaleY', 'angle', 'depth', 'alpha', 'visible',
  'scrollFactorX', 'scrollFactorY', 'blendMode', 'lit', 'filters',
] as const;

export const TYPE_FIELDS: Record<CoreType, readonly string[]> = {
  image: ['texture', 'originX', 'originY', 'flipX', 'flipY', 'tint'],
  vector: ['texture', 'colors', 'originX', 'originY', 'tint'],
  sprite: ['texture', 'originX', 'originY', 'flipX', 'flipY', 'tint', 'animation'],
  text: ['text', 'themeStyle', 'style', 'originX', 'originY'],
  bitmapText: ['font', 'text', 'fontSize', 'align', 'letterSpacing', 'lineSpacing', 'maxWidth', 'tint', 'originX', 'originY'],
  nineSlice: ['texture', 'width', 'height', 'leftWidth', 'rightWidth', 'topHeight', 'bottomHeight', 'tint', 'originX', 'originY'],
  zone: ['width', 'height', 'originX', 'originY'],
  tileSprite: [
    'texture', 'width', 'height', 'originX', 'originY', 'flipX', 'flipY', 'tint',
    'tilePositionX', 'tilePositionY', 'tileScaleX', 'tileScaleY', 'scrollSpeedX', 'scrollSpeedY',
  ],
  particles: [
    'texture', 'frames', 'burst', 'emitting', 'frequency', 'quantity', 'maxAlive', 'stopAfter',
    'lifespan', 'speed', 'direction', 'gravityX', 'gravityY', 'rotate', 'scale', 'particleAlpha', 'color', 'ease', 'zone',
  ],
  light: ['color', 'intensity', 'radius', 'z'],
  video: ['key', 'autoplay', 'loop', 'muted', 'playbackRate', 'originX', 'originY', 'flipX', 'flipY', 'tint'],
  container: ['children'],
  tilemapLayer: ['map', 'layer'],
  prefab: ['prefab', 'overrides'],
};

export const COMMON_FIELDS = ['id', 'type', 'name', 'clips', 'scripts', 'connections', 'data'] as const;

/**
 * Whether an object holds children: a container, or a plugin type whose
 * entries carry a `children` list (such as a UI stack that lays them out).
 */
export function holdsChildren(o: { type: string; children?: unknown }): boolean {
  return o.type === 'container' || (o.type !== 'prefab' && !isCoreType(o.type) && Array.isArray(o.children));
}
